import { useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/useToast';
import { validateRequest } from '../validation';
import type { RequestDraft, RequestErrors } from '../validation';

const directions = [
  'requests.direction.natural',
  'requests.direction.humanities',
  'requests.direction.technical',
] as const;

const emptyDraft: RequestDraft = {
  topic: '',
  direction: directions[0],
  deadline: '',
  comment: '',
};

export const RequestForm = () => {
  const { t } = useI18n();
  const { notify } = useToast();
  const [draft, setDraft] = useState<RequestDraft>(emptyDraft);
  const [errors, setErrors] = useState<RequestErrors>({});

  const update = (field: keyof RequestDraft) => (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    setDraft((current) => ({ ...current, [field]: event.target.value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validateRequest(draft);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    notify(t('requests.form.submitted'), 'success');
    setDraft(emptyDraft);
  };

  return (
    <form className="form-grid" onSubmit={handleSubmit} noValidate>
      <label className="field wide2">
        {t('requests.form.topic')}
        <input
          type="text"
          value={draft.topic}
          placeholder={t('requests.form.topicPlaceholder')}
          aria-invalid={Boolean(errors.topic)}
          aria-describedby={errors.topic ? 'request-topic-error' : undefined}
          onChange={update('topic')}
        />
        {errors.topic && (
          <span className="field-error" id="request-topic-error">
            {t(errors.topic)}
          </span>
        )}
      </label>

      <label className="field">
        {t('requests.form.direction')}
        <select value={draft.direction} onChange={update('direction')}>
          {directions.map((direction) => (
            <option key={direction} value={direction}>
              {t(direction)}
            </option>
          ))}
        </select>
      </label>

      <label className="field">
        {t('requests.form.deadline')}
        <input
          type="date"
          value={draft.deadline}
          aria-invalid={Boolean(errors.deadline)}
          aria-describedby={errors.deadline ? 'request-deadline-error' : undefined}
          onChange={update('deadline')}
        />
        {errors.deadline && (
          <span className="field-error" id="request-deadline-error">
            {t(errors.deadline)}
          </span>
        )}
      </label>

      <label className="field wide4">
        {t('requests.form.comment')}
        <textarea
          value={draft.comment}
          placeholder={t('requests.form.commentPlaceholder')}
          onChange={update('comment')}
        />
      </label>

      <div className="wide4">
        <button type="submit" className="btn-primary">
          {t('requests.form.submit')}
        </button>
      </div>
    </form>
  );
};
